import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

export interface WorkTime {
	timeSheetApprovalWorkTimesId: number
	startTime: string
	endTime: string
	isClean: boolean
}

export interface BonusCode {
	codeId: number
	code: string
	description: string
}

export interface Document {
	fileName: string
	content: string
}

interface Driver {
	driverId: number
	internalNumber: number
	driverName: string
	companyName: string | null
	canUseApp: boolean
}

export interface KmsVehicle {
	kmsId?: number
	kmsIni: number
	kmsEnd: number
	inEmpty: boolean
}

export interface ServiceExecution {
	serviceId?: number
	startKms?: number
	endKms?: number
	serviceStartKms?: number
	serviceEndKms?: number
	pax?: number
	isClean?: boolean
	comments?: string
	startDateTime?: string
	endDateTime?: string
	startTimeErrorComment: string | null
	endTimeErrorComment: string | null
	paxErrorComment: string | null
	isReserve?: boolean
	kmsVehicles?: KmsVehicle[]
	continuedServicePosition?: number
	continuedServiceTotal?: number
	continuedServiceStartDate?: string
	continuedServiceEndDate?: string
	isAU?: boolean
}

export interface ServiceOriginal extends ServiceExecution {
	clientName: string
	origin: string
	destination: string
	kmsFull: number
	kmsEmpty: number
	vehicle: Vehicle
	reference: string
	contract: string
	seconddriver?: boolean
}

interface Vehicle {
	licencePlate: string
	fleetNumber: string
	kmsInServices: number
	kmsOutsideServices: number
	kmsTotal: number
}

export interface Services {
	isExpanded?: boolean
	service: ServiceOriginal
	serviceExecution?: ServiceExecution
	mainSecundaryDriver: Driver | null
	mainSecundaryDriverTimeSheetId: number | null
	mainSecundaryDriverTimeSheetStatus: string | null
}

export interface InitialStatus {
	kmsId: number
	kmsIni: number | null
	regDate: string | null
	inEmpty: boolean
	isClean: boolean
}

export interface Approval {
	timeSheetApprovalId: number
	timeSheetApprovalDate: string
	startKms: number
	endKms: number
	comments: string
	submited: boolean
	approvalCode: number
	userName: string | null
	dateTimeReg: string
	refund: number
	workTypeId: number
	timeSheetDate: string | null
	extraTimePayment: boolean
	driverPauseTypeId: number | null
	refunds: string | null
	timeSheetApprovalCodes: BonusCode[]
	timeSheetApprovalWorkTimes: WorkTime[]
}

export interface TimeSheetPageState {
	timeSheetDate: string
	comments: string
	isSubmitted: boolean
	isBlocked: boolean
	timeSheetId: number
	workTypeId: number
	dispRecalculateOnSave?: boolean
	dispDrivingBreak: number | null
	uniqueAgent: string | null
	status?: string
	initialStatus: InitialStatus | null
	driver: Driver
	services: Services[]
	vehicles: Vehicle[]
	workTimes: WorkTime[]
	extraTimePayment: boolean | null
	bonusCodes: BonusCode[]
	approval?: Approval
	documents?: Document[]
	editedBy: string | null
	editedDate: string | null
	refunds: string | null
	refund: number | null
	rowVersion: number
}

export const initialState: TimeSheetPageState = {
	extraTimePayment: true,
	timeSheetDate: new Date().toISOString(),
	comments: '',
	isSubmitted: false,
	workTypeId: 0,
	isBlocked: false,
	timeSheetId: 0,
	status: '',
	dispDrivingBreak: null,
	uniqueAgent: null,
	editedBy: null,
	editedDate: null,
	refunds: null,
	refund: null,
	rowVersion: Math.floor(Math.random() * Number.MAX_SAFE_INTEGER),
	driver: {
		driverId: 0,
		internalNumber: 0,
		driverName: '',
		companyName: '',
		canUseApp: true,
	},
	initialStatus: {
		kmsId: 0,
		kmsIni: 0,
		regDate: new Date().toISOString(),
		inEmpty: false,
		isClean: false,
	},
	services: [
		{
			isExpanded: false,
			service: {
				serviceId: 0,
				startKms: 0,
				endKms: 0,
				serviceStartKms: 0,
				serviceEndKms: 0,
				pax: 0,
				isClean: false,
				comments: '',
				startDateTime: new Date().toISOString(),
				endDateTime: new Date().toISOString(),
				startTimeErrorComment: null,
				endTimeErrorComment: null,
				paxErrorComment: null,
				isReserve: false,
				clientName: 'Contoso Ltd.',
				origin: 'Lisboa',
				destination: 'Porto',
				kmsFull: 0,
				kmsEmpty: 0,
				reference: 'xyz12345',
				contract: 'Contract XYZ',
				seconddriver: undefined,
				continuedServiceStartDate: undefined,
				continuedServiceEndDate: undefined,
				kmsVehicles: [
					{
						kmsId: 0,
						kmsIni: 0,
						kmsEnd: 0,
						inEmpty: true,
					},
				],
				vehicle: {
					licencePlate: 'AA-00-00',
					fleetNumber: '1234',
					kmsInServices: 0,
					kmsOutsideServices: 0,
					kmsTotal: 0,
				},
			},
			serviceExecution: {
				serviceId: 0,
				startKms: 0,
				endKms: 0,
				serviceStartKms: 0,
				serviceEndKms: 0,
				pax: 0,
				isClean: false,
				comments: '',
				startDateTime: new Date().toISOString(),
				endDateTime: new Date().toISOString(),
				startTimeErrorComment: null,
				endTimeErrorComment: null,
				paxErrorComment: null,
				isReserve: false,
				kmsVehicles: [
					{
						kmsId: 0,
						kmsIni: 0,
						kmsEnd: 0,
						inEmpty: true,
					},
				],
			},
			mainSecundaryDriver: null,
			mainSecundaryDriverTimeSheetId: null,
			mainSecundaryDriverTimeSheetStatus: null,
		},
	],
	vehicles: [
		{
			licencePlate: 'AA-00-00',
			fleetNumber: '1234',
			kmsInServices: 0,
			kmsOutsideServices: 0,
			kmsTotal: 0,
		},
	],
	workTimes: [
		{
			timeSheetApprovalWorkTimesId: 0,
			startTime: new Date().toISOString(),
			endTime: new Date().toISOString(),
			isClean: true,
		},
	],
	bonusCodes: [
		{
			codeId: 0,
			code: '0',
			description: 'Bonus Code Description',
		},
	],
	documents: [],
	approval: {
		timeSheetApprovalId: 0,
		timeSheetApprovalDate: new Date().toISOString(),
		startKms: 0,
		endKms: 0,
		comments: '',
		submited: false,
		approvalCode: 0,
		userName: '',
		dateTimeReg: new Date().toISOString(),
		refund: 0,
		workTypeId: 0,
		timeSheetDate: new Date().toISOString(),
		extraTimePayment: true,
		driverPauseTypeId: null,
		refunds: '',
		timeSheetApprovalCodes: [
			{
				codeId: 0,
				code: '0',
				description: 'Approval Code Description',
			},
		],
		timeSheetApprovalWorkTimes: [
			{
				timeSheetApprovalWorkTimesId: 0,
				startTime: new Date().toISOString(),
				endTime: new Date().toISOString(),
				isClean: true,
			},
		],
	},
}

export const timesheetPageSlice = createSlice({
	name: 'timesheetPage',
	initialState,
	reducers: {
		// * refactor: changing only the properties that are being updated to avoid overwriting unchanged data
		handleDetails: (state, action: PayloadAction<Partial<TimeSheetPageState>>) => {
			Object.assign(state, action.payload)
		},
		handleTimesheetApprovalDetails: (state, action: PayloadAction<Partial<Approval>>) => {
			if (state.approval) {
				Object.assign(state.approval, action.payload)
			}
		},
		handleTimesheetDriverDetails: (state, action: PayloadAction<Partial<Driver>>) => {
			Object.assign(state.driver, action.payload)
		},
		handleTimesheetInitialStatusDetails: (state, action: PayloadAction<Partial<InitialStatus>>) => {
			if (state.initialStatus) {
				Object.assign(state.initialStatus, action.payload)
			}
		},
		handleTimesheetServicesDetails: (state, action: PayloadAction<Partial<Services>[]>) => {
			if (action.payload.length > 0) {
				// Update only the services that have changed based on serviceId
				action.payload.forEach(updatedService => {
					const existingServiceIndex = state.services.findIndex(
						service => service.service.serviceId === updatedService.service?.serviceId,
					)
					if (existingServiceIndex !== -1) {
						state.services[existingServiceIndex] = {
							...state.services[existingServiceIndex],
							...updatedService,
						}
					} else {
						if (updatedService.service?.serviceId !== undefined) {
							state.services.push(updatedService as Services)
						}
					}
				})
			}
		},
		handleTimesheetVehiclesDetails: (state, action: PayloadAction<Partial<Vehicle>[]>) => {
			if (action.payload.length > 0) {
				// Update only the vehicles that have changed based on licencePlate
				action.payload.forEach(updatedVehicle => {
					const existingVehicleIndex = state.vehicles.findIndex(
						vehicle => vehicle.licencePlate === updatedVehicle.licencePlate,
					)
					if (existingVehicleIndex !== -1) {
						state.vehicles[existingVehicleIndex] = {
							...state.vehicles[existingVehicleIndex],
							...updatedVehicle,
						}
					} else {
						if (updatedVehicle.licencePlate !== undefined) {
							state.vehicles.push(updatedVehicle as Vehicle)
						}
					}
				})
			}
		},
		handleTimesheetWorkTimesDetails: (state, action: PayloadAction<Partial<WorkTime>[]>) => {
			if (action.payload.length > 0) {
				// Update only the work times that have changed based on timeSheetApprovalWorkTimesId
				action.payload.forEach(updatedWorkTime => {
					const existingWorkTimeIndex = state.workTimes.findIndex(
						workTime => workTime.timeSheetApprovalWorkTimesId === updatedWorkTime.timeSheetApprovalWorkTimesId,
					)
					if (existingWorkTimeIndex !== -1) {
						state.workTimes[existingWorkTimeIndex] = {
							...state.workTimes[existingWorkTimeIndex],
							...updatedWorkTime,
						}
					} else {
						// Only push if timeSheetApprovalWorkTimesId is defined
						if (updatedWorkTime.timeSheetApprovalWorkTimesId !== undefined) {
							state.workTimes.push(updatedWorkTime as WorkTime)
						}
					}
				})
			}
		},
	},
})

export const {
	handleDetails,
	handleTimesheetApprovalDetails,
	handleTimesheetDriverDetails,
	handleTimesheetInitialStatusDetails,
	handleTimesheetServicesDetails,
	handleTimesheetVehiclesDetails,
	handleTimesheetWorkTimesDetails,
} = timesheetPageSlice.actions
